import { env } from '../../config/env'
import { isNativeApp } from '../../utils/platform'
import { registerWebPushSubscription } from '../appsync-client'

/**
 * Browser Web Push registration (closed-tab notifications via VAPID).
 *
 * This is the WEB counterpart to the native `push-registration` service. It
 * subscribes the active service worker's PushManager to our VAPID application
 * server key and registers the resulting subscription with the backend (platform
 * WEB) so the alert fan-out Lambdas can deliver OS notifications even when the
 * PWA tab is closed. The service-worker `push` handler renders them.
 *
 * Boundaries / safety:
 *  - No-op on native (Capacitor uses FCM/APNs, not this), on browsers without
 *    the Push API, and when no VAPID public key is configured.
 *  - Requires Notification permission to already be `granted` — we never prompt
 *    here; the guided permission onboarding owns the prompt.
 *  - Idempotent + concurrency-guarded, so it is safe to call on every mount and
 *    after a permission grant.
 */

let inFlight: Promise<void> | null = null

/** True when this browser can do Web Push and we have a key to subscribe with. */
export function webPushSupported(): boolean {
  return (
    !isNativeApp() &&
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** This browser's current Web Push endpoint, or null when not subscribed. */
export async function getWebPushEndpoint(): Promise<string | null> {
  if (!webPushSupported()) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = await registration?.pushManager.getSubscription()
    return subscription?.endpoint ?? null
  } catch {
    return null
  }
}

/** Decode a base64url VAPID public key into the Uint8Array subscribe() expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const buffer = new ArrayBuffer(raw.length)
  const output = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

async function syncSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON()
  const endpoint = json.endpoint
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!endpoint || !p256dh || !auth) return
  await registerWebPushSubscription({ endpoint, p256dh, auth })
}

/**
 * Ensure the browser is subscribed to Web Push and the subscription is
 * registered with the backend. Safe to call repeatedly.
 *
 * @param opts.force re-subscribe from scratch (used after a
 * `pushsubscriptionchange` drops the old subscription).
 */
export async function registerForWebPush(opts?: { force?: boolean }): Promise<void> {
  if (!webPushSupported()) return
  if (!env.vapidPublicKey) {
    console.warn('[web-push] VITE_VAPID_PUBLIC_KEY not set — skipping web push subscription')
    return
  }
  if (Notification.permission !== 'granted') return
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const registration = await navigator.serviceWorker.ready
      let subscription = await registration.pushManager.getSubscription()

      if (subscription && opts?.force) {
        await subscription.unsubscribe().catch(() => {})
        subscription = null
      }

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(env.vapidPublicKey),
        })
      }

      await syncSubscription(subscription)
    } catch (err) {
      console.warn('[web-push] subscribe/register failed', err)
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}
